// src/api/dish.api.ts

import { get, post, put, del, patch, upload, uploadPut } from './client';
import {
  ApiResponse,
  PaginatedResponse,
  Dish,
  DishAddOn,
  CreateDishDto,
  UpdateDishDto,
  DishQueryParams,
  DishStatus,
  SetUnavailabilityDto,
  AvailabilitySchedule,
  CreateDishAddOnDto,
  UpdateDishAddOnDto,
} from '../types';

export const dishApi = {
  getAllDishes: (params?: DishQueryParams) => {
    return get<PaginatedResponse<Dish>>('/dishes', { params });
  },

  getDishById: (id: string, includeAllAddOns?: boolean) => {
    return get<ApiResponse<Dish>>(`/dishes/${id}`, {
      params: includeAllAddOns ? { includeAllAddOns: 'true' } : {},
    });
  },

  createDish: (data: CreateDishDto) => {
    const formData = new FormData();
    
    formData.append('name', data.name);
    formData.append('price', data.price.toString());
    
    if (data.description) {
      formData.append('description', data.description);
    }
    
    if (data.status) {
      formData.append('status', data.status);
    }
    
    if (data.categoryId) {
      formData.append('categoryId', data.categoryId);
    }

    if (data.preparationTime !== undefined) {
      formData.append('preparationTime', data.preparationTime.toString());
    }

    if (data.templateId !== undefined) {
      formData.append('templateId', data.templateId === null ? '' : data.templateId);
    }

    if (data.productGroup !== undefined) {
      formData.append('productGroup', data.productGroup || '');
    }

    if (data.variantName !== undefined) {
      formData.append('variantName', data.variantName || '');
    }

    if (data.image) {
      formData.append('image', data.image);
    }

    return upload<ApiResponse<Dish>>('/dishes', formData);
  },

  updateDish: (id: string, data: UpdateDishDto) => {
    const formData = new FormData();
    
    if (data.name !== undefined) {
      formData.append('name', data.name);
    }
    
    if (data.price !== undefined) {
      formData.append('price', data.price.toString());
    }
    
    if (data.description !== undefined) {
      formData.append('description', data.description);
    }
    
    if (data.status !== undefined) {
      formData.append('status', data.status);
    }
    
    if (data.categoryId !== undefined) {
      formData.append('categoryId', data.categoryId === null ? '' : data.categoryId);
    }

    if (data.preparationTime !== undefined) {
      formData.append('preparationTime', data.preparationTime.toString());
    }

    if (data.templateId !== undefined) {
      formData.append('templateId', data.templateId === null ? '' : data.templateId);
    }

    if (data.productGroup !== undefined) {
      formData.append('productGroup', data.productGroup || '');
    }

    if (data.variantName !== undefined) {
      formData.append('variantName', data.variantName || '');
    }

    if (data.image) {
      formData.append('image', data.image);
    }

    return uploadPut<ApiResponse<Dish>>(`/dishes/${id}`, formData);
  },

  deleteDish: (id: string) => {
    return del<ApiResponse>(`/dishes/${id}`);
  },

  bulkUpdateStatus: (dishIds: string[], status: DishStatus) => {
    return patch<ApiResponse>('/dishes/bulk-status', { dishIds, status });
  },

  reorderDishes: (dishes: { id: string; displayOrder: number }[]) => {
    return patch<ApiResponse>('/dishes/reorder', { dishes });
  },

  // Availability API functions
  setTemporaryUnavailability: (id: string, data: SetUnavailabilityDto) => {
    return post<ApiResponse<Dish>>(`/dishes/${id}/unavailability`, data);
  },

  clearTemporaryUnavailability: (id: string) => {
    return del<ApiResponse<Dish>>(`/dishes/${id}/unavailability`);
  },

  updateAvailabilitySchedule: (id: string, schedule: AvailabilitySchedule | null) => {
    return put<ApiResponse<Dish>>(`/dishes/${id}/schedule`, { availabilitySchedule: schedule });
  },

  getDishWithAvailability: (id: string) => {
    return get<ApiResponse<Dish>>(`/dishes/${id}/availability`);
  },

  // ============= Dish Add-ons API =============

  getDishAddOns: (dishId: string) => {
    return get<ApiResponse<DishAddOn[]>>(`/dishes/${dishId}/addons`);
  },

  createDishAddOn: (dishId: string, data: CreateDishAddOnDto) => {
    return post<ApiResponse<DishAddOn>>(`/dishes/${dishId}/addons`, data);
  },

  updateDishAddOn: (dishId: string, addOnId: string, data: UpdateDishAddOnDto) => {
    return put<ApiResponse<DishAddOn>>(`/dishes/${dishId}/addons/${addOnId}`, data);
  },

  deleteDishAddOn: (dishId: string, addOnId: string) => {
    return del<ApiResponse>(`/dishes/${dishId}/addons/${addOnId}`);
  },

  reorderDishAddOns: (dishId: string, addOns: { id: string; sortOrder: number }[]) => {
    return patch<ApiResponse>(`/dishes/${dishId}/addons/reorder`, { addOns });
  },

  bulkUpdateDishAddOns: (dishId: string, addOns: Array<Partial<DishAddOn> & { id?: string }>) => {
    return put<ApiResponse<DishAddOn[]>>(`/dishes/${dishId}/addons`, { addOns });
  },
};